import axios, { AxiosResponse } from 'axios';
import { createDataObject } from '../lib/utils';
import { CommissionType } from './salesCommision';
import { isSettlementTransferLookupFailure, SettlementTransferLookupError } from '../features/settlementClassification/transferLookup';

export /**
 * @method get
 * @brief 급여 계산 조회
 * https://github.com/mustMoveProject/rappo-project/blob/RAPPO-1201/src/docs/SalaryCalc.md#%EA%B8%89%EC%97%AC-%EA%B3%84%EC%82%B0-%EC%A1%B0%ED%9A%8C
 */
const getSalaryCalcs = async ({
    year,
    month,
    selectedCoachId,
    centerId,
    accessToken,
}: GetSalaryCalcsParams): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    if (!selectedCoachId) {
        return { status: 400 } as any;
    }
    try {
        const params = new URLSearchParams();
        params.append('year', String(year));
        params.append('month', String(month));
        params.append('selectedCoachId', String(selectedCoachId));
        response = await axios({
            method: 'get',
            url: `${process.env.REACT_APP_SERVER}/v1/salarycalcs/center/${centerId}?${params.toString()}`,
            headers: {
                authorization: accessToken,
            },
        });

        if (response.status === 200) {
            return response;
        }
    } catch (error: any) {
        // 재등록 수업료용 양도 조회 실패(503) — 0원 화면 대신 오류 상태로 보이게 던진다(캐시되지 않는다).
        if (isSettlementTransferLookupFailure(error?.response?.status, error?.response?.data)) {
            throw new SettlementTransferLookupError();
        }
        // Error 😨
        switch (error.response.status) {
            case 400:
                return { status: error.response.status };
            case 401:
                return { status: error.response.status };
            case 404:
                return { status: error.response.status };
            case 500:
                alert('서버 내부 오류');
                return { status: error.response.status };
        }
    }
};
/**
 * @method get
 * @brief 급여 계산 조회 — 「전체 강사」 표 전용.
 * 기존 getSalaryCalcs 와 같은 엔드포인트·같은 파라미터다. 다른 점은 표가 강사 수만큼 부르므로
 *  - 500 에서 알림창을 띄우지 않는다(강사 수만큼 뜬다). 실패는 표의 그 줄에 「불러오지 못함」으로 보인다.
 *  - 모든 오류(응답 없는 네트워크 오류 포함)를 { status, errorCode } 로 돌려준다 — 성공 판정은 호출부가 응답 본문으로 한다.
 *  - 달·센터가 바뀌면 진행 중 요청을 끊을 수 있게 signal 을 받는다.
 */
const getSalaryCalcsForTable = async ({
    year,
    month,
    selectedCoachId,
    centerId,
    accessToken,
    signal,
}: GetSalaryCalcsParams & { signal?: AbortSignal }): Promise<any> => {
    if (!selectedCoachId) {
        return { status: 400 };
    }
    try {
        const params = new URLSearchParams();
        params.append('year', String(year));
        params.append('month', String(month));
        params.append('selectedCoachId', String(selectedCoachId));
        return await axios({
            method: 'get',
            url: `${process.env.REACT_APP_SERVER}/v1/salarycalcs/center/${centerId}?${params.toString()}`,
            headers: {
                authorization: accessToken,
            },
            signal,
        });
    } catch (error: any) {
        return {
            status: error?.response?.status ?? 0,
            errorCode: error?.response?.data?.errorCode,
            canceled: error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED',
        };
    }
};
export { getSalaryCalcsForTable };

export /**
 * @method post
 * @brief 급여 계산 수정
 * https://github.com/mustMoveProject/rappo-project/blob/RAPPO-1201/src/docs/SalaryCalc.md#%EA%B8%89%EC%97%AC-%EA%B3%84%EC%82%B0-%EC%88%98%EC%A0%95
 */
const postSalaryCalcs = async ({
    year,
    month,
    incentive,
    incentiveType,
    baseSalary,
    etcBonus,
    isTaxable,
    revenueAdjustments,
    selectedCoachId,
    centerId,
    accessToken,
}: PostSalaryCalcsParams & { accessToken: string; centerId: string }): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    if (!selectedCoachId) {
        return { status: 400 } as any;
    }

    try {
        const params = new URLSearchParams();
        params.append('selectedCoachId', String(selectedCoachId));
        response = await axios({
            method: 'post',
            url: `${process.env.REACT_APP_SERVER}/v1/salarycalcs/center/${centerId}?${params.toString()}`,
            headers: {
                authorization: accessToken,
            },
            data: {
                year,
                month,
                incentive,
                incentiveType,
                baseSalary,
                etcBonus,
                isTaxable,
                // 조회 응답의 항목에는 DB 하위문서 _id 가 붙어 온다. 서버 검증은 모르는 키를
                // 거절(400)하므로 계약에 있는 세 필드만 골라 보낸다.
                revenueAdjustments: (revenueAdjustments ?? []).map(({ reasonType, label, amount }) => ({
                    reasonType,
                    label,
                    amount,
                })),
            },
        });

        return response;
    } catch (error: any) {
        // 알림은 호출부가 상태별로 띄운다. 응답이 없는 네트워크 오류는 status 0.
        return {
            status: error?.response?.status ?? 0,
            code: error?.response?.data?.code,
            errorCode: error?.response?.data?.errorCode,
            message: error?.response?.data?.message,
        };
    }
};

export type GetSalaryCalcsParams = {
    year: string;
    month: string;
    selectedCoachId: string;
    centerId: string;
    accessToken: string;
};

// 개별 보너스 타입
// amountType: 'fixed' | 'percentage'
//  - 'fixed'      : amount 를 원(KRW) 단위 고정 금액으로 해석
//  - 'percentage' : amount 를 센터 매출(settlement) 대비 비율(0~100)로 해석
// vatIncluded (percentage 전용):
//  - true  (기본) : 매출 기준값을 VAT 포함 금액으로 사용
//  - false        : 매출 기준값을 VAT 제외 금액(공급가액)으로 사용
// 레거시 데이터는 amountType 미존재 → 'fixed'로 폴백, vatIncluded 미존재 → true
export type EtcBonusAmountType = 'fixed' | 'percentage';
export interface EtcBonus {
    name: string;
    amount: number;
    amountType?: EtcBonusAmountType;
    vatIncluded?: boolean;
}

// sessionTier 구간별 집계 항목
export interface SessionBracketSummary {
    minSessions: number;
    maxSessions: number | null;
    commissionRate: number; // 퍼센트
    sessionCount: number; // 이번 달 해당 구간에 떨어진 수업 수
    totalFee: number; // 해당 구간 수업료 합계
}

// 매출 조정 (Revenue Adjustment)
//  - reasonType 'refund'  : 환불 금액 프리셋. 백엔드가 산출한 refundAmount 를 그대로 amount 로 저장(부호 포함).
//  - reasonType 'custom'  : 관리자가 라벨/금액 직접 입력.
// amount 는 양수/음수 모두 허용 (음수는 차감).
export type RevenueAdjustmentReasonType = 'refund' | 'custom';
export interface RevenueAdjustment {
    reasonType: RevenueAdjustmentReasonType;
    label: string;
    amount: number;
}

// 매출 구간 (DetailedCommissionTab 의 ScheduleBracket 과 동일 — 응답에 함께 내려옴)
export interface SalaryCalcScheduleBracket {
    minRevenue: number; // 만원 단위
    maxRevenue: number | null; // 만원 단위, 마지막 구간은 null
    commissionRate: number; // 퍼센트
}

// salaryCalcData 타입
export interface SalaryCalcData {
    year: string; // 연도
    month: string; // 월
    incentive: number; // 인센티브 (고정 값 또는 비율)
    incentiveType: CommissionType;
    baseSalary: number; // 기본 급여
    etcBonus: EtcBonus[]; // 추가 보너스 목록
    isTaxable: boolean; // 과세 여부
    _id?: string; // 고유 ID (옵션)
    totalScheduleFee?: number; // 총 스케줄 수수료
    groupScheduleFee?: number; // 그룹 수업료
    totalSales?: number; // 총 판매 수수료
    groupSales?: number; // 그룹 수업 수강권 매출액
    inScheduleCount: number; // 근무시간 내 수업 건수

    inScheduleCommissionRate?: number; // 근무시간 내 수업 요율
    totalInScheduleFee?: number; // 근무시간 내 수업 수업료
    outScheduleCount?: number; // 근무시간 외 수업 건수
    outScheduleCommissionRate?: number; // 근무시간 외 수업 요율
    totalOutScheduleFee?: number; //근무시간 외 수업 수업료
    outAchievedBracketMinRevenue?: number; // 근무시간 외 수업 누적매출 달성액 ( 충족된 매출구간 min값 )
    /**
     * 신서버(상세 정산) additive — 구간표로만 계산한 수업(appliedRule === 'tier')의 IN/OUT 건수·금액.
     * 위 in/outScheduleCount·totalIn/OutScheduleFee 는 예외 수업료·개별 요율 수업까지 포함한다.
     * 있으면 IN/OUT 줄은 이 값을 보이고, 예외·개별 요율 수업은 appliedRuleSummary 에 보인다(features/salaryCalc/tierInOutDisplay).
     */
    inTierScheduleCount?: number;
    inTierScheduleFee?: number;
    outTierScheduleCount?: number;
    outTierScheduleFee?: number;
    /** 신서버 additive — 그 강사 그 달 계산에 서버가 실제 쓴 요율 방식. 상세 정산(tier)이면 tier…, 세션별이면 session… */
    tierCommissionType?: 'percentage' | 'fixed';
    sessionCommissionType?: 'percentage' | 'fixed';

    // sessionTier 전용 (settlementType === 'sessionTier'일 때만 내려옴)
    totalSessionCount?: number;
    bracketBreakdown?: SessionBracketSummary[];
    /**
     * 예외 수업료가 적용된 PT 수업 수·수업료 합(신서버, 예외 수업이 1건 이상일 때만 온다).
     * 이 수업들은 구간 소계(bracketBreakdown)에서 빠진다 — 서버 값 그대로 한 줄로 보인다.
     */
    exceptionScheduleCount?: number;
    totalExceptionScheduleFee?: number;

    // ── 매출 구간 표 (tier 정산용) — 백엔드 확장 예정 ──
    inScheduleBrackets?: SalaryCalcScheduleBracket[];
    outScheduleBrackets?: SalaryCalcScheduleBracket[];

    // ── 매출 조정 — 백엔드 확장 예정 ──
    // 최종 매출은 프론트가 totalSales + groupSales + totalRevenueAdjustment 로 직접 합산한다.
    // (서버는 effectiveSales 를 별도 필드로 내려주지 않는다)
    refundAmount?: number; // 자동 환불 합계(원). 차감 부호 포함(예: -200000)
    revenueAdjustments?: RevenueAdjustment[]; // 저장된 매출 조정 항목
    totalRevenueAdjustment?: number; // sum(revenueAdjustments[].amount). 서버가 함께 내려주면 그대로 사용, 없으면 프론트 계산

    // ── 적용 규칙별 PT 수업료 소계 (tier 센터 + 신서버의 base·sessionTier 예외가 있을 때) — 서버 값 그대로 표시만 한다 ──
    appliedRuleSummary?: AppliedRuleSummaryItem[];

    // ── 「시간 상관없이 IN」(상세 정산) — 값이 있을 때만 서버가 싣는다. 화면은 건수만 보인다 ──
    /** 노쇼라서 근무 시간 밖인데 IN 으로 계산한 PT 수업(전체) */
    noshowForcedInScheduleCount?: number;
    noshowForcedInScheduleFee?: number;
    /** 그중 구간표로만 계산한 수업(appliedRule === 'tier') — inTierScheduleCount 의 일부. IN 줄 주석용 */
    noshowForcedInTierScheduleCount?: number;
    noshowForcedInTierScheduleFee?: number;
    /** 「항상 IN 요율의 배수」 예외로 근무 시간 밖인데 IN 으로 계산한 PT 수업 */
    ruleForcedInScheduleCount?: number;
    ruleForcedInScheduleFee?: number;
    /** 시간과 상관없이 IN 으로 계산했는데 IN 요율이 0이라 0원이 된 PT 수업 */
    forcedInZeroRateScheduleCount?: number;
    /** 그룹 — 노쇼라서 근무 시간 밖인데 IN 으로 계산한 예약 */
    groupNoshowForcedInScheduleCount?: number;
    groupNoshowForcedInScheduleFee?: number;
    /** 그룹 — 위 예약 중 IN 요율이 0이라 0원이 된 예약 */
    groupForcedInZeroRateScheduleCount?: number;
}

/** 급여 조회 응답 본문 — salaryCalcData 의 형제 필드로 공유 수업 무결성 오류가 올 수 있다(HTTP 200). */
export interface SalaryCalcResponseBody {
    salaryCalcData?: SalaryCalcData;
    /** 정산에서 제외된 공유 수업 목록 — 있으면 그 강사 수업료에서 일부 수업이 빠졌다는 뜻 */
    settlementIntegrityErrors?: unknown[];
}

export interface AppliedRuleSummaryItem {
    /** 'custom' | 'category:<key>' | 'reRegistration' | 'tier' */
    appliedRule: string;
    scheduleCount: number;
    totalScheduleFee: number;
    /** 「항상 IN 요율의 배수」 예외 행에만 — 'inMultiplier' 와 그 배수 */
    exceptionMode?: 'inMultiplier';
    exceptionValue?: number;
}
// SalaryCalcData 에서 _id 를 제외한 나머지 필수 값들, selectedCoachId 는 추가
export type PostSalaryCalcsParams = Omit<
    SalaryCalcData,
    | '_id'
    | 'inScheduleCount'
    | 'inScheduleCommissionRate'
    | 'totalInScheduleFee'
    | 'outScheduleCount'
    | 'outScheduleCommissionRate'
    | 'totalOutScheduleFee'
    | 'outAchievedBracketMinRevenue'
    | 'inTierScheduleCount'
    | 'inTierScheduleFee'
    | 'outTierScheduleCount'
    | 'outTierScheduleFee'
    | 'tierCommissionType'
    | 'sessionCommissionType'
    | 'inScheduleBrackets'
    | 'outScheduleBrackets'
    | 'refundAmount'
    | 'totalRevenueAdjustment'
    | 'appliedRuleSummary'
    | 'exceptionScheduleCount'
    | 'totalExceptionScheduleFee'
    | 'noshowForcedInScheduleCount'
    | 'noshowForcedInScheduleFee'
    | 'noshowForcedInTierScheduleCount'
    | 'noshowForcedInTierScheduleFee'
    | 'ruleForcedInScheduleCount'
    | 'ruleForcedInScheduleFee'
    | 'forcedInZeroRateScheduleCount'
    | 'groupNoshowForcedInScheduleCount'
    | 'groupNoshowForcedInScheduleFee'
    | 'groupForcedInZeroRateScheduleCount'
> & {
    selectedCoachId: string;
};
